import type { FieldError, SubmitErrorHandler } from 'react-hook-form'
import { Form, useActionData, useLoaderData } from 'react-router'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useFieldArray } from 'react-hook-form'
import { useRemixForm } from 'remix-hook-form'
import cc from 'classcat'

import type {
  OrderWithdrawalActionData,
  OrderWithdrawalFormData,
  OrderWithdrawalLoaderData,
} from '../../views/OrderWithdrawal'
import { resolver } from '../../views/OrderWithdrawal'
import { useScrollInvalidElementIntoView } from '../../utils/scrollInvalidElementIntoView'
import { useTranslate, withComponents } from '../../utils/translate'

const MAX_ITEMS = 30

export default function OrderWithdrawalForm(): ReactElement {
  const t = useTranslate('shop', 'components.orderWithdrawalForm')
  const scrollInvalidElementIntoView = useScrollInvalidElementIntoView()
  const actionData = useActionData<OrderWithdrawalActionData>()
  const { csrfToken } = useLoaderData<OrderWithdrawalLoaderData>()

  const handleInvalidSubmit = useCallback<SubmitErrorHandler<OrderWithdrawalFormData>>(
    (_errors, event) => {
      const form = event?.currentTarget as HTMLFormElement | undefined

      requestAnimationFrame(() => {
        scrollInvalidElementIntoView(() => {
          const firstInvalidElement = form?.querySelector<HTMLElement>('[aria-invalid="true"], :invalid:not(fieldset)')
          firstInvalidElement?.focus()

          return firstInvalidElement
        })
      })
    },
    [scrollInvalidElementIntoView],
  )

  const methods = useRemixForm<OrderWithdrawalFormData>({
    defaultValues: {
      items: [{ name: '', quantity: 1 }],
      withdrawalScope: 'completeOrder',
      _csrf: csrfToken,
      ...actionData?.defaultValues,
    },
    mode: 'onSubmit',
    resetOptions: {
      keepErrors: false,
    },
    resolver,
    shouldUnregister: true,
    submitHandlers: {
      onInvalid: handleInvalidSubmit,
    },
  })

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = methods

  const { fields, append, remove } = useFieldArray({ control, name: 'items' })

  const withdrawalScope = watch('withdrawalScope') ?? 'completeOrder'
  const hasErrors = Object.keys(errors).length > 0
  const hasServerError = Boolean(actionData?.serverError)
  const hasOrderNotFoundError = Boolean(actionData?.orderNotFound)
  const formErrorRef = useRef<HTMLDivElement>(null)
  const showFormErrorNotification = hasErrors || hasServerError || hasOrderNotFoundError

  const renderFormError = () => {
    if (hasOrderNotFoundError) return t('orderNotFoundError')

    if (hasServerError) {
      return (
        <>
          {t('serverError')}
          {actionData?.requestId && <code> {actionData.requestId}</code>}
        </>
      )
    }

    return t('errorMessage')
  }

  useEffect(() => {
    if (!showFormErrorNotification) return
    requestAnimationFrame(() => {
      scrollInvalidElementIntoView(() => formErrorRef.current)
    })
  }, [showFormErrorNotification, scrollInvalidElementIntoView])

  const renderFieldError = (id: string, className: string, error?: FieldError) => (
    <div id={id} className={cc([className, { 'validation-styling-hidden': !error?.message }])} role="alert">
      {error?.message ? t(error.message as Parameters<typeof t>[0]) : ''}
    </div>
  )

  return (
    <div className="order-withdrawal-wrapper">
      <h2>{t('title')}</h2>
      <p>{t('description')}</p>
      <Form method="POST" noValidate className="order-withdrawal-form" onSubmit={handleSubmit} aria-busy={isSubmitting}>
        {showFormErrorNotification && (
          <div className="order-withdrawal-error-notification" role="alert" ref={formErrorRef}>
            <span>{renderFormError()}</span>
          </div>
        )}
        <div>
          <label className="order-withdrawal-form-label">
            {t('firstNameInputField.label')}
            <span aria-hidden="true">*</span>
            <input
              className={cc(['order-withdrawal-form-field', { invalid: Boolean(errors.firstName) }])}
              type="text"
              maxLength={320}
              autoComplete="given-name"
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-invalid={Boolean(errors.firstName)}
              aria-describedby="order-withdrawal-first-name-error"
              {...register('firstName')}
            />
            {renderFieldError(
              'order-withdrawal-first-name-error',
              'order-withdrawal-error-validation',
              errors.firstName,
            )}
          </label>
        </div>
        <div>
          <label className="order-withdrawal-form-label">
            {t('lastNameInputField.label')}
            <span aria-hidden="true">*</span>
            <input
              className={cc(['order-withdrawal-form-field', { invalid: Boolean(errors.lastName) }])}
              type="text"
              maxLength={320}
              autoComplete="family-name"
              required
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby="order-withdrawal-last-name-error"
              {...register('lastName')}
            />
            {renderFieldError('order-withdrawal-last-name-error', 'order-withdrawal-error-validation', errors.lastName)}
          </label>
        </div>
        <div>
          <label className="order-withdrawal-form-label">
            {t('emailInputField.label')}
            <span aria-hidden="true">*</span>
            <input
              className={cc(['order-withdrawal-form-field', { invalid: Boolean(errors.email) }])}
              type="email"
              maxLength={320}
              autoComplete="email"
              required
              aria-invalid={Boolean(errors.email)}
              aria-describedby="order-withdrawal-email-error"
              {...register('email')}
            />
            {renderFieldError('order-withdrawal-email-error', 'order-withdrawal-error-validation', errors.email)}
          </label>
        </div>
        <div>
          <label className="order-withdrawal-form-label">
            {t('orderNumberInputField.label')}
            <span aria-hidden="true">*</span>
            <input
              className={cc(['order-withdrawal-form-field', { invalid: Boolean(errors.orderNumber) }])}
              type="text"
              maxLength={40}
              required
              aria-invalid={Boolean(errors.orderNumber)}
              aria-describedby="order-withdrawal-order-number-error"
              {...register('orderNumber')}
            />
            {renderFieldError(
              'order-withdrawal-order-number-error',
              'order-withdrawal-error-validation',
              errors.orderNumber,
            )}
          </label>
        </div>
        <fieldset className="order-withdrawal-form-selection">
          <legend>{t('withdrawalScopeRadioGroup.title')}</legend>
          <label className="order-withdrawal-form-radio">
            <input
              type="radio"
              value="completeOrder"
              {...register('withdrawalScope', { disableProgressiveEnhancement: true })}
              defaultChecked
            />
            {t('withdrawalScopeRadioGroup.completeOrderOption.label')}
          </label>
          <label className="order-withdrawal-form-radio">
            <input
              type="radio"
              value="individualItems"
              {...register('withdrawalScope', { disableProgressiveEnhancement: true })}
            />
            {t('withdrawalScopeRadioGroup.individualItemsOption.label')}
          </label>
          {withdrawalScope === 'individualItems' && (
            <>
              <span className="order-withdrawal-form-items-hint">{t('individualItemsSection.hint')}</span>
              <div className="order-withdrawal-form-items">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    role="group"
                    className="order-withdrawal-form-item"
                    aria-label={t('individualItemsSection.item.ariaLabel', { number: index + 1 })}
                  >
                    <div className="order-withdrawal-form-name-wrapper">
                      <input
                        className={cc([
                          'order-withdrawal-form-field',
                          { invalid: Boolean(errors.items?.[index]?.name) },
                        ])}
                        type="text"
                        maxLength={320}
                        required
                        aria-label={t('individualItemsSection.productNameInputField.ariaLabel')}
                        aria-invalid={Boolean(errors.items?.[index]?.name)}
                        aria-describedby={`order-withdrawal-item-name-${index}-error`}
                        {...register(`items.${index}.name` as const)}
                      />
                      {renderFieldError(
                        `order-withdrawal-item-name-${index}-error`,
                        'order-withdrawal-error-validation',
                        errors.items?.[index]?.name,
                      )}
                    </div>
                    <div className="order-withdrawal-form-quantity-wrapper">
                      <input
                        className={cc([
                          'order-withdrawal-form-quantity-field',
                          { invalid: Boolean(errors.items?.[index]?.quantity) },
                        ])}
                        type="number"
                        min={1}
                        aria-label={t('individualItemsSection.quantityInputField.ariaLabel')}
                        required
                        aria-invalid={Boolean(errors.items?.[index]?.quantity)}
                        aria-describedby={`order-withdrawal-item-quantity-${index}-error`}
                        {...register(`items.${index}.quantity` as const, { valueAsNumber: true })}
                        defaultValue={1}
                      />
                      {renderFieldError(
                        `order-withdrawal-item-quantity-${index}-error`,
                        'order-withdrawal-error-validation',
                        errors.items?.[index]?.quantity,
                      )}
                    </div>
                    <button
                      className="order-withdrawal-form-remove-item"
                      type="button"
                      aria-label={t('individualItemsSection.removeItemButton.tooltip')}
                      title={t('individualItemsSection.removeItemButton.tooltip')}
                      onClick={() => remove(index)}
                      {...(index === 0 && { style: { visibility: 'hidden' } })}
                    ></button>
                  </div>
                ))}
                {fields.length < MAX_ITEMS && (
                  <button
                    className="order-withdrawal-form-add-item"
                    type="button"
                    onClick={() => append({ name: '', quantity: 1 })}
                  >
                    {t('individualItemsSection.addItemButton.label')}
                  </button>
                )}
              </div>
            </>
          )}
        </fieldset>
        <div className="order-withdrawal-form-contract">
          <label className={cc({ invalid: Boolean(errors.contractWithdrawal) })}>
            <input
              type="checkbox"
              value="on"
              required
              aria-invalid={Boolean(errors.contractWithdrawal)}
              aria-describedby="order-withdrawal-contract-withdrawal-error"
              {...register('contractWithdrawal', { disableProgressiveEnhancement: true })}
            />
            {t('contractWithdrawalCheckbox.label')}
          </label>
          <div
            id="order-withdrawal-contract-withdrawal-error"
            className={cc([
              'order-withdrawal-error-validation',
              { 'validation-styling-hidden': !errors.contractWithdrawal?.message },
            ])}
            role="alert"
          >
            {errors.contractWithdrawal?.message && (
              <span>
                {withComponents(t)(errors.contractWithdrawal.message as Parameters<typeof t>[0], {
                  confirmButtonLabel: <em>{t('confirmButton.label')}</em>,
                })}
              </span>
            )}
          </div>
        </div>
        <label className="box-of-sweet-nectar">
          {t('honeypotInputField.label')}
          <input autoComplete="off" tabIndex={-1} {...register('_tac')} defaultValue="" />
        </label>
        <input type="hidden" {...register('_csrf')} />
        <button
          className={cc(['order-withdrawal-form-submit', { pending: isSubmitting }])}
          type="submit"
          disabled={isSubmitting}
        >
          {t('confirmButton.label')}
        </button>
      </Form>
    </div>
  )
}
